import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { CtwrRequestService } from '../../ctwr.service';
import { AuthenticationService } from '../../../../auth.service';
import { FilterableService } from '../../../mesc/filterable-service';
import { FormGroup } from '@angular/forms';
import { SignalrService, WfTask, SIGNALR } from '../../signalr.service';
import { cgpAuthentication } from '../../../../../modules/cgp-authentication';
import { RequestStatus } from '../../workflow.service';
import { HttpParams } from '@angular/common/http';

import { BaseService } from '../../../mesc/mesc.service';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';

@Injectable()
export class RequestDetailService {
    private readonly baseService: BaseService;
    private readonly router: Router;
    private readonly authService: AuthenticationService;
    private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
    private readonly signalRService: SignalrService;
    private refreshInterval: any;
    private refreshingView = false;
    requestService: CtwrRequestService;

    requestDetail: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    partApprovalData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    workApprovalData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    primaryPart: FilterableService = new FilterableService();
    impactedParts: FilterableService = new FilterableService();
    history: FilterableService = new FilterableService();
    module: any;
    isUpdating: boolean;
    RequestNumber: string;
    isCtwrAdmin: boolean;
    isPteTm: boolean;
    wfTask: any;
    loggedInUser: any = cgpAuthentication.getUserInfo();

    constructor(injector: Injector) {
        this.authService = injector.get(AuthenticationService);
        this.baseService = injector.get(BaseService);
        this.signalRService = injector.get(SignalrService);
        this.requestService = injector.get(CtwrRequestService);
        this.router = injector.get(Router);

        const _roles = this.authService.getUserRoles();
        this.isPteTm = _roles.some(r => (r.endsWith('TM') || r.endsWith('PTE')));

        this.isCtwrAdmin = this.authService.isClaimValid('/CTWR/Administration');

        this.authService.getActiveModule()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(x => this.module = x);

        this.requestDetail
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(x => x ? this.RequestNumber = x.RequestNumber : null);
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }


    /** 
     * 
     */
    reset() {
        this.requestDetail.next(null);
        this.partApprovalData.next(null);
        this.history.next(null);
    }


    /**
     * 
     * @param wfTaskId 
     * @param form 
     */
    addTask(wfTaskId: string, form: FormGroup, panelbar: any) {
        const task = {
            id: wfTaskId,
            form,
            panelbar
        } as WfTask;
        this.signalRService.addTask(task);
    }


    /**
     * 
     */
    refreshRequest(isRefresh = false): Promise<any> {
        return this.requestService.read(`/Detail/ByRequestNumber?requestNumber=${this.RequestNumber}&isRefresh=${isRefresh}`)
            .then(response => {
                if (!response) this.router.navigate(['']);

                this.requestDetail.next(response);
                this.refreshView();
            });
    }


    /**
     * 
     */
    refreshHistory(milestones = false): Promise<any> {
        const _path = milestones ?
            '/RequestHistory/Milestones?RequestId=' : '/GetRequestHistory?RequestId=';
        return this.requestService.read(_path + this.requestDetail.value.RequestId, true)
            .then(x => {
                x.map(h => {
                    h.Date = new DatePipe('en-US')
                        .transform(h.Date, 'short');
                    return h;
                });
                this.history.next(x);
            });
    }


    /**
     * 
     */
    refreshPartApprovalMatrix(): Promise<any> {
        this.partApprovalData.next(true);
        return this.requestService
            .read(`/PartApprovalMatrix?requestId=${this.requestDetail.value.RequestId}`, false)
            .then(response => {
                if (response) {
                    const locApp: any[] = response.LocationApprovals
                    if (locApp && this.requestDetail.value.RequestStatus === RequestStatus.AWAITING_APPROVAL && locApp.length < 1) {
                        if (!this.refreshInterval) this.refreshInterval = setInterval(() => this.refreshPartApprovalMatrix(), 2000);
                    } else {
                        if (this.refreshInterval) clearInterval(this.refreshInterval);
                        this.initializeSignalR();
                    }
                }

                this.partApprovalData.next(response);
                return response;
            })
            .catch(e => {
                this.requestService.handleError(e);
                this.partApprovalData.next(null);
            });
    }


    /**
     * 
     */
    refreshWorkApprovalMatrix() {
        this.workApprovalData.next(true);
        if (this.wfTask) {
            this.refreshInterval = setInterval(() => this.refreshWorkApprovalAsync(), 2000);
        } else {
            this.refreshWorkApproval();
        }
    }


    /**
     * 
     */
    private refreshWorkApproval() {
        this.wfTask = null;
        this.requestService
            .read(`/WorkApprovalMatrix?requestId=${this.requestDetail.value.RequestId}`, false)
            .then(response => {
                if (this.requestDetail.value.RequestStatus === RequestStatus.APPROVED && !response) {
                    this.refreshWorkApproval();
                }
                this.workApprovalData.next(response);
                this.initializeSignalR();
            })
            .catch(e => {
                this.workApprovalData.next(null);
                this.requestService.handleError(e);
            });
    }


    /**
     * 
     */
    private refreshWorkApprovalAsync() {
        let _params: HttpParams;
        let _path: string;
        if (this.wfTask.RequestItemId) {
            _path = '/IsWapItemCancelComplete?';
            _params = new HttpParams()
                .set('requestItemd', this.wfTask.RequestItemId);
        } else {
            _path = '/IsWorkApprovalReady?';
            _params = new HttpParams()
                .set('requestId', this.wfTask.RequestId)
                .set('componentCatId', this.wfTask.ComponentCategoryId)
                .set('componentId', this.wfTask.ComponentId);
        }

        this.requestService.read(`${_path}${_params}`)
            .then(x => {
                if (x) {
                    clearInterval(this.refreshInterval);
                    this.refreshWorkApproval();
                }
            });
    }


    /**
     * 
     */
    mapUsers = (data: any[]): any[] => {
        if (data) {
            return data.map(i => {
                i.cn = `${i.FirstName} ${i.LastName}`;
                return i;
            });
        } else {
            return [];
        }
    }


    /**
     * 
     * @param response 
     */
    private refreshLocalRequest(response: any) {
        const gmin = this.loggedInUser.gmin || this.loggedInUser.gmuniqueidentifier;
        if (gmin === response.gmin) {
            if (response.action === "RequestItemCancelled" ||
                response.action === "RequestItemDeleted" ||
                this.requestDetail.value.RequestStatus === RequestStatus.APPROVED ||
                this.requestDetail.value.RequestStatus === RequestStatus.AWAITING_APPROVAL
            ) {
                this.refreshWorkApprovalMatrix();
            }
            this.refreshRequest(true);
        }

    }


    /**
     * 
     */
    private initializeSignalR() {
        if (this.signalRService.connection) return;
        this.signalRService.initialize()
            .then(() => {
                this.signalRService.connection
                    .on(SIGNALR.refreshEvent, (x) => this.refreshLocalRequest(x));
            })
    }


    /**
     * 
     */
    private refreshView() {
        if (this.refreshingView) return;
        const request = this.requestDetail.value;
        this.refreshingView = true;
        Promise.all([
            this.baseService.read(`ctwr/Part/${request.PrimaryPartId}?requestId=${request.RequestId}`),
            this.baseService.read(`ctwr/Part/impactedparts/${request.PrimaryPartId}?requestId=${request.RequestId}`),
            this.refreshHistory()
        ]).then(([r1, r2]) => {
            this.refreshingView = false;
            this.primaryPart.next([r1]);
            this.impactedParts.next(r2);
        });
    }

}
